import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ThemePicker from './components/shared/ThemePicker';
import Sidebar from './components/shared/Sidebar';
import UpgradePrompt from './components/shared/UpgradePrompt';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import Dashboard from './components/dashboard/Dashboard';
import WorkoutTracker from './components/workout/WorkoutTracker';
import MuscleExplorer from './components/workout/MuscleExplorer';
import NutritionTracker from './components/nutrition/NutritionTracker';
import WeightTracker from './components/weight/WeightTracker';
import MedicationTracker from './components/medication/MedicationTracker';
import HealthTracker from './components/health/HealthTracker';
import ProgressPage from './components/progress/ProgressPage';
import Settings from './components/shared/Settings';
import './styles/global.css';

function ProtectedLayout({ children, hideSidebar }) {
  const { user, isPaid } = useAuth();
  const { theme } = useTheme();
  if (!user) return <Navigate to="/login" />;
  const immersive = hideSidebar && isPaid;
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: theme.bg, '--bg': theme.bg, '--bg-secondary': theme.bgSecondary, '--bg-card': theme.bgCard, '--border': theme.border, '--text': theme.text, '--text-secondary': theme.textSecondary, '--accent': theme.accent, '--success': theme.success, '--warning': theme.warning, '--danger': theme.danger }}>
      {!immersive && <Sidebar />}
      <main style={{ flex: 1, overflowX: 'hidden' }}>{children}</main>
    </div>
  );
}

// Renders children for paid users; shows UpgradePrompt for free users.
// Free tier: Dashboard, Weight Tracker, Health Vitals, Settings.
// Paid tier ($9.99/mo): Workout, Nutrition, Medication, Progress unlocked.
function GatedRoute({ children, featureName }) {
  const { isPaid } = useAuth();
  return isPaid ? children : <UpgradePrompt featureName={featureName} />;
}

function AppContent() {
  const { theme, showPicker } = useTheme();
  const { user } = useAuth();

  if (showPicker) return <ThemePicker />;
  if (!theme) return null;

  const cssVars = {
    '--bg': theme.bg, '--bg-secondary': theme.bgSecondary, '--bg-card': theme.bgCard,
    '--border': theme.border, '--text': theme.text, '--text-secondary': theme.textSecondary,
    '--accent': theme.accent, '--success': theme.success, '--warning': theme.warning, '--danger': theme.danger,
  };

  return (
    <div style={cssVars}>
      <Toaster position="top-right" toastOptions={{
        style: { background: theme.bgCard, color: theme.text, border: `1px solid ${theme.border}` },
      }} />
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
        <Route path="/signup" element={user ? <Navigate to="/dashboard" /> : <Signup />} />
        <Route path="/dashboard" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
        <Route path="/workout" element={<ProtectedLayout><GatedRoute featureName="Workout Tracker"><WorkoutTracker /></GatedRoute></ProtectedLayout>} />
        <Route path="/muscles" element={<ProtectedLayout hideSidebar><GatedRoute featureName="Muscle Explorer"><MuscleExplorer /></GatedRoute></ProtectedLayout>} />
        <Route path="/nutrition" element={<ProtectedLayout><GatedRoute featureName="Nutrition Tracker"><NutritionTracker /></GatedRoute></ProtectedLayout>} />
        <Route path="/weight" element={<ProtectedLayout><WeightTracker /></ProtectedLayout>} />
        <Route path="/medications" element={<ProtectedLayout><GatedRoute featureName="Medication Tracker"><MedicationTracker /></GatedRoute></ProtectedLayout>} />
        <Route path="/health" element={<ProtectedLayout><HealthTracker /></ProtectedLayout>} />
        <Route path="/progress" element={<ProtectedLayout><GatedRoute featureName="Progress Dashboard"><ProgressPage /></GatedRoute></ProtectedLayout>} />
        <Route path="/settings" element={<ProtectedLayout><Settings /></ProtectedLayout>} />
        <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
